import useProviderStore from '@hooks/app/useProviderStore';
import useKycStatus from '@hooks/query/kyc/useKycStatus';
import useUsersProviders from '@hooks/query/provider/useUsersProviders';
import useUserWizards from '@hooks/query/user/useUserWizards';
import React from 'react';
import { isKycApproved } from 'types/authKyc.types';
import { IUserProviderItem } from 'types/provider.types';

/**
 * `kyc` -< show kyc onboarding
 * `provider` -> show provider onboarding
 * `dashboard` -> show dashboard
 */
type TUserState = 'kyc' | 'provider' | 'dashboard' | null | 'error';
type TRet = [TUserState, React.Dispatch<React.SetStateAction<TUserState>>, () => void];

export function useUserDashboardState(): TRet {
  const { data: kycData, refetch: kycRefetch, isError: kycError } = useKycStatus();
  const kycApproved = kycData?.status ? isKycApproved(kycData.status) : undefined;

  const { data: wizards, refetch: wizardRefetch, isError: wizardsError } = useUserWizards();
  const {
    data: providers,
    refetch: providerRefetch,
    isError: providersError,
  } = useUsersProviders();
  const { setCurrentProvider } = useProviderStore();
  const hasError = kycError || wizardsError || providersError;

  const [userState, setUserState] = React.useState<TUserState>(null);

  const refetch = React.useCallback(() => {
    kycRefetch();
    wizardRefetch();
    providerRefetch();
  }, []);

  React.useEffect(() => {
    if (hasError) {
      setUserState('error');
      return;
    }

    if (!kycData || !wizards || !providers) {
      return;
    }

    if (!kycApproved) {
      /**
       * If expert doesn't have kyc, redirect to kyc nav
       */
      setUserState('kyc');
      return;
    }

    const providersResult = providers;
    let approvedProvider: IUserProviderItem | undefined = undefined;

    /**
     * User has no providers, or first time visiting.
     * Show provider onboarding.
     */
    if (
      !Array.isArray(providersResult) ||
      providersResult.length === 0 ||
      (providersResult.length > 0 && !wizards?.providerWizard) ||
      (approvedProvider = providersResult?.find(
        (p) => p.status === 'approved' && p.provider.status === 'approved',
      )) === undefined
    ) {
      setUserState('provider');
      return;
    }

    /**
     * First time in wizard, and user was invited.
     * Show list and continue to dashboard
     */
    if (approvedProvider) {
      setCurrentProvider(approvedProvider);
      setUserState('dashboard');
      return;
    }
  }, [kycData, providers, wizards, hasError]);

  return [userState, setUserState, refetch];
}
